import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    input,
    Output,
    signal,
    untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { Message } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { AdminGame } from '@/lib-backend';
import { FakeReviewService } from '@/app/services/fake-review.service';
import { NotificationService } from '@/app/services/notification.service';
import { effectiveTitle } from '@/app/utils/game';
import {
    buildBellDistribution,
    resolveGameAutoTags,
} from './review-distribution';

const DEFAULT_RANGE_DAYS = 30;
/** Distribution concentration is fixed at maximum (peak around the target). */
const CONCENTRATION = 1;
const RATING_MIN = 1;
const RATING_MAX = 5;

type BulkStatus = 'ok' | 'skipped' | 'error';

interface BulkResult {
    title: string;
    status: BulkStatus;
    detail: string;
}

/**
 * Bulk fake-review generation across every game in the realm. The operator
 * picks a range for the average rating and a range for the review count;
 * each game then gets a value drawn at random from those ranges, so games
 * look naturally varied rather than identical.
 *
 * Reuses the single-game per-game endpoint client-side: for each game it
 * resolves the game's own tags, then calls replaceGameReviews with the
 * game-specific rating distribution and the shared created-at range. Replace
 * is destructive — existing fake reviews on each game are removed first — so
 * a confirm is required.
 */
@Component({
    selector: 'app-bulk-generate-reviews-dialog',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        DatePicker,
        Dialog,
        InputNumber,
        Message,
        TableModule,
        Tag,
    ],
    templateUrl: './bulk-generate-reviews-dialog.html',
})
export class BulkGenerateReviewsDialog {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }

    readonly realmId = input.required<string>();
    readonly games = input<AdminGame[]>([]);

    @Output() visibleChange = new EventEmitter<boolean>();
    /** Emitted once a bulk run completes (so the host can refresh if needed). */
    @Output() changed = new EventEmitter<void>();

    private readonly fakeReviewService = inject(FakeReviewService);
    private readonly notification = inject(NotificationService);

    protected readonly visibleSignal = signal(false);

    // ── Per-game randomization ranges ───────────────────────────────────────
    protected readonly averageMin = signal(4);
    protected readonly averageMax = signal(5);
    protected readonly countMin = signal(5);
    protected readonly countMax = signal(20);
    protected readonly createdFrom = signal<Date | null>(null);
    protected readonly createdTo = signal<Date | null>(null);

    protected readonly ratingMin = RATING_MIN;
    protected readonly ratingMax = RATING_MAX;

    // ── Run state ───────────────────────────────────────────────────────────
    protected readonly confirmVisible = signal(false);
    protected readonly running = signal(false);
    protected readonly finished = signal(false);
    protected readonly progress = signal(0);
    protected readonly currentTitle = signal('');
    protected readonly results = signal<BulkResult[]>([]);

    protected readonly total = computed(() => this.games().length);

    protected readonly validationError = computed<string | null>(() => {
        const aMin = this.averageMin();
        const aMax = this.averageMax();
        if (
            !Number.isFinite(aMin) ||
            !Number.isFinite(aMax) ||
            aMin < RATING_MIN ||
            aMax > RATING_MAX
        ) {
            return `Average rating must be within [${RATING_MIN}..${RATING_MAX}].`;
        }
        if (aMin > aMax) {
            return 'Average rating: min must be ≤ max.';
        }
        const cMin = this.countMin();
        const cMax = this.countMax();
        if (!Number.isFinite(cMin) || !Number.isFinite(cMax) || cMin < 1) {
            return 'Review count must be at least 1.';
        }
        if (cMin > cMax) {
            return 'Review count: min must be ≤ max.';
        }
        const from = this.createdFrom();
        const to = this.createdTo();
        if (!from || !to) {
            return 'Pick a created-at range.';
        }
        if (from > to) {
            return 'Created-at: from must be ≤ to.';
        }
        return null;
    });

    protected readonly disabled = computed(
        () =>
            this.running() ||
            this.total() === 0 ||
            this.validationError() !== null,
    );

    protected readonly okCount = computed(
        () => this.results().filter((r) => r.status === 'ok').length,
    );
    protected readonly skippedCount = computed(
        () => this.results().filter((r) => r.status === 'skipped').length,
    );
    protected readonly errorCount = computed(
        () => this.results().filter((r) => r.status === 'error').length,
    );

    constructor() {
        effect(() => {
            if (this.visibleSignal()) {
                untracked(() => this.resetForm());
            }
        });
    }

    private resetForm(): void {
        this.averageMin.set(4);
        this.averageMax.set(5);
        this.countMin.set(5);
        this.countMax.set(20);
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - DEFAULT_RANGE_DAYS);
        this.createdFrom.set(from);
        this.createdTo.set(to);
        this.confirmVisible.set(false);
        this.running.set(false);
        this.finished.set(false);
        this.progress.set(0);
        this.currentTitle.set('');
        this.results.set([]);
    }

    // ── Dialog lifecycle ──────────────────────────────────────────────────────

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
    }

    protected close(): void {
        // Don't allow closing mid-run.
        if (this.running()) return;
        this.onVisibleChange(false);
    }

    // ── Run ───────────────────────────────────────────────────────────────────

    protected requestRun(): void {
        if (this.disabled()) return;
        this.confirmVisible.set(true);
    }

    protected cancelRun(): void {
        this.confirmVisible.set(false);
    }

    protected async confirmRun(): Promise<void> {
        this.confirmVisible.set(false);
        await this.run();
    }

    private async run(): Promise<void> {
        const games = this.games();
        if (games.length === 0 || this.running()) return;

        const aMin = this.averageMin();
        const aMax = this.averageMax();
        const cMin = this.countMin();
        const cMax = this.countMax();
        const createdFrom = (this.createdFrom() ?? new Date()).toISOString();
        const createdTo = (this.createdTo() ?? new Date()).toISOString();

        this.running.set(true);
        this.finished.set(false);
        this.progress.set(0);
        this.results.set([]);

        const collected: BulkResult[] = [];
        try {
            for (const game of games) {
                const title = effectiveTitle(game);
                this.currentTitle.set(title);

                const tags = resolveGameAutoTags(game);
                if (tags.length === 0) {
                    collected.push({
                        title,
                        status: 'skipped',
                        detail: 'No tags resolved for this game.',
                    });
                    this.progress.update((p) => p + 1);
                    continue;
                }

                const avg = randomFloat(aMin, aMax);
                const count = randomInt(cMin, cMax);
                const ratingCounts = buildBellDistribution(
                    avg,
                    count,
                    CONCENTRATION,
                );

                const outcome = await this.fakeReviewService.replaceGameReviews(
                    {
                        realmId: this.realmId(),
                        gameId: game.Id,
                        ratingCounts,
                        tags,
                        createdFrom,
                        createdTo,
                    },
                );
                collected.push(this.toResult(title, avg, outcome));
                this.progress.update((p) => p + 1);
            }
        } finally {
            this.results.set(collected);
            this.currentTitle.set('');
            this.running.set(false);
            this.finished.set(true);
            this.changed.emit();
            this.notify(collected);
        }
    }

    private toResult(
        title: string,
        avg: number,
        outcome: Awaited<ReturnType<FakeReviewService['replaceGameReviews']>>,
    ): BulkResult {
        switch (outcome.result) {
            case 'Ok':
                return {
                    title,
                    status: 'ok',
                    detail: `Removed ${outcome.removedCount}, created ${outcome.createdCount} (~${avg.toFixed(1)}★).`,
                };
            case 'InsufficientAuthors':
                return {
                    title,
                    status: 'skipped',
                    detail: `Not enough authors: need ${outcome.requestedAuthors}, have ${outcome.availableAuthors}.`,
                };
            case 'InsufficientComments':
                return {
                    title,
                    status: 'skipped',
                    detail: `Not enough ${outcome.commentsRating}★ comments for the tags: need ${outcome.requestedComments}, have ${outcome.availableComments}.`,
                };
            default:
                return { title, status: 'error', detail: 'Request failed.' };
        }
    }

    private notify(results: BulkResult[]): void {
        const ok = results.filter((r) => r.status === 'ok').length;
        const skipped = results.filter((r) => r.status === 'skipped').length;
        const errored = results.filter((r) => r.status === 'error').length;
        this.notification.show({
            severity: errored > 0 ? 'warn' : 'success',
            summary: 'Bulk review generation finished',
            detail: `${ok} game(s) updated, ${skipped} skipped, ${errored} failed.`,
        });
    }

    protected statusSeverity(
        status: BulkStatus,
    ): 'success' | 'warn' | 'danger' {
        if (status === 'ok') return 'success';
        if (status === 'skipped') return 'warn';
        return 'danger';
    }

    protected trackByIndex(index: number): number {
        return index;
    }
}

/** Uniform random float in [min, max]. */
function randomFloat(min: number, max: number): number {
    if (max <= min) return min;
    return min + Math.random() * (max - min);
}

/** Uniform random integer in [min, max] (inclusive). */
function randomInt(min: number, max: number): number {
    const lo = Math.ceil(min);
    const hi = Math.floor(max);
    if (hi <= lo) return lo;
    return lo + Math.floor(Math.random() * (hi - lo + 1));
}
